import React, { SyntheticEvent, useContext, useEffect, useRef, useState, memo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import {
  Autocomplete,
  Box,
  Button,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Tooltip
} from '@mui/material';
import { DialogContent } from '@material-ui/core';
import { ArrowBack } from '@mui/icons-material';
import Actions from '../../store/actions';
import { getProductDataQuality, updateDataQuality } from '../../api/pmda';
import Store from '../../store';
import { qcStatuses } from './utils';

const EditTranslationQuality = () => {
  const { dispatch } = useContext<any>(Store);
  const { yjCode } = useParams() as any;
  const history = useHistory();
  const [productData, setProductData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const pdfIframeRef = useRef<HTMLIFrameElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [qcStatus, setQcStatus] = useState('');

  const fetchPageCuration = async () => {
    try {
      setLoading(true);
      const result = await getProductDataQuality(yjCode);

      if (result.status !== 200 || !result?.data?.body) {
        throw new Error('Error: Product Resource not available');
      }

      setProductData(result.data.body);
      setPdfUrl(result.data.body?.s3_url ?? '');
      setQcStatus(result.data.body?.translation_check_status ?? '');
    } catch (error) {
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: (error as Error).message }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const payload = {
        ...productData,
        translation_check_status: qcStatus
      };

      const result = await updateDataQuality(yjCode, payload);

      if (result.status === 200)
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Data updated', color: 'success' }
        });
      else
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Error Updating data' }
        });
    } catch (error) {
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: (error as Error).message }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevious = () => {
    const yjCodesFromLocalStorage = localStorage.getItem('pmdaDataQualityYjCodeList');
    if (!yjCodesFromLocalStorage) {
      return;
    }
    const allYjCodes = JSON.parse(yjCodesFromLocalStorage);
    const currentIndex = allYjCodes.indexOf(yjCode);
    if (currentIndex === 0 || currentIndex === -1) {
      return;
    }
    const previousVin = allYjCodes[currentIndex - 1];
    history.push(`/pmda/quality/translation/${previousVin}`);
  };
  const handleNext = () => {
    const yjCodesFromLocalStorage = localStorage.getItem('pmdaDataQualityYjCodeList');
    if (!yjCodesFromLocalStorage) {
      return;
    }
    const allYjCodes = JSON.parse(yjCodesFromLocalStorage);
    const currentIndex = allYjCodes.indexOf(yjCode);
    if (currentIndex === allYjCodes.length - 1 || currentIndex === -1) {
      return;
    }
    const nextVin = allYjCodes[currentIndex + 1];
    history.push(`/pmda/quality/translation/${nextVin}`);
  };

  useEffect(() => {
    fetchPageCuration();
  }, [yjCode]);

  return (
    <Box sx={{ overflow: 'hidden', mt: 10 }}>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Stack direction='row' justifyContent='space-between'>
          <Button onClick={handlePrevious} fullWidth variant='outlined'>
            Previous
          </Button>
          <Typography
            component='p'
            variant='h5'
            style={{
              color: '#3AB09E',
              fontWeight: 'bold',
              fontSize: '28px',
              minWidth: 300,
              textAlign: 'center'
            }}>
            Translation Quality
          </Typography>
          <Button onClick={handleNext} fullWidth variant='outlined'>
            Next
          </Button>
        </Stack>
        <Tooltip title='Go Back' placement='right'>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => history.push('/pmda/quality/translations')}>
            Back to main page
          </Button>
        </Tooltip>
      </DialogTitle>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <DialogContent>
          <Grid container spacing={2} display='flex'>
            <Grid item xs={12} lg={7} sx={{ overflow: 'auto', height: '70vh', padding: 2 }}>
              <Stack gap={2}>
                <Box>
                  Drug name( trade name):
                  <Stack direction='row' gap={1} mt={1}>
                    <TextField
                      variant='outlined'
                      fullWidth
                      label='English'
                      id='trade_name'
                      defaultValue={productData?.trade_name}
                      value={productData?.trade_name}
                      onChange={e =>
                        setProductData((prevData: any) => ({
                          ...prevData,
                          trade_name: e.target.value
                        }))
                      }
                    />
                    <TextField
                      variant='outlined'
                      fullWidth
                      disabled
                      label='Japanese'
                      id='trade_name_jp'
                      defaultValue={productData?.trade_name_jp}
                      value={productData?.trade_name_jp}
                      onChange={e =>
                        setProductData((prevData: any) => ({
                          ...prevData,
                          trade_name_jp: e.target.value
                        }))
                      }
                    />
                  </Stack>
                </Box>
                <Box>
                  Generic name(active ingredient):
                  <Stack direction='row' gap={1} mt={1}>
                    <TextField
                      variant='outlined'
                      fullWidth
                      label='English'
                      id='generic_name'
                      defaultValue={productData?.active_ingredient}
                      value={productData?.active_ingredient}
                      onChange={e =>
                        setProductData((prevData: any) => ({
                          ...prevData,
                          active_ingredient: e.target.value
                        }))
                      }
                    />
                    <TextField
                      variant='outlined'
                      fullWidth
                      disabled
                      label='Japanese'
                      id='generic_name_original'
                      defaultValue={productData?.active_ingredient_jp}
                      value={productData?.active_ingredient_jp}
                      onChange={e =>
                        setProductData((prevData: any) => ({
                          ...prevData,
                          active_ingredient_jp: e.target.value
                        }))
                      }
                    />
                  </Stack>
                </Box>

                <Autocomplete
                  id='qc_status_product'
                  fullWidth
                  options={qcStatuses.map(status => status.toLowerCase())}
                  value={qcStatus}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setQcStatus(newValue);
                    }
                  }}
                  sx={{ fontSize: '2vh' }}
                  renderInput={(
                    params // eslint-disable-next-line react/jsx-props-no-spreading
                  ) => <TextField {...params} label='QC Status' variant='outlined' required />}
                />

                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  variant='contained'
                  color='primary'>
                  {submitting ? 'Submitting' : 'Submit Data Changes'}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={5} sx={{ height: '70vh' }}>
              {pdfUrl ? (
                <iframe
                  ref={pdfIframeRef}
                  src={pdfUrl}
                  title='Product Resource'
                  style={{ height: '100%', width: '100%' }}
                />
              ) : (
                <Stack
                  style={{
                    height: '100%',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <Typography>No package inserts found</Typography>
                </Stack>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      )}
    </Box>
  );
};
export default memo(EditTranslationQuality);
